import { useTranslations } from 'next-intl';
import { useRef } from 'react';
import { InputField } from '@/app/components/shared/ElementDialog/components/InputField';
import { TextAreaField } from '@/app/components/shared/ElementDialog/components/TextAreaField';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogTitle } from '@/app/components/ui/dialog';
import { toast } from '@/app/components/ui/use-toast';
import type { InputRefType } from '@/app/types';

export const UpdateItemDialog = ({
  open,
  setOpen,
  onSave,
  label,
  initValue,
  type = 'input',
  placeholder,
  errorMsg,
  maxLength,
  rows,
  required,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  label: string;
  initValue: string;
  onSave: (value: string) => Promise<void>;
  type?: 'input' | 'textarea';
  placeholder?: string;
  errorMsg?: string;
  maxLength?: number;
  rows?: number;
  required?: boolean;
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const inputRef = useRef<InputRefType>(null);

  const t = useTranslations();
  const handleSave = () => {
    const val =
      (type === 'input' ? inputRef.current?.getInputValue()?.trim() : textareaRef.current?.value?.trim()) || '';
    if (!required || (required && val)) {
      if (maxLength && val.length > maxLength) {
        toast({
          type: 'error',
          message: t('elements.elementDialog.inputMaxLength', {
            n: maxLength,
          }),
        });
      } else {
        onSave(val);
      }
    } else {
      toast({
        type: 'error',
        message: `${label} ${t('elements.elementDialog.required')}`,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="p-6 sm:p-6 md:p-6"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
        }}
        closable={false}
        disableOverlayClose={true}
      >
        <DialogTitle className="flex items-center">
          {required && <span className="mr-1 text-system-danger">*</span>}
          {label}
        </DialogTitle>
        {type === 'input' ? (
          <InputField
            fieldClassName="bg-system-bg03 h-12"
            ref={inputRef}
            initialValue={initValue}
            readOnly={false}
            placeholder={placeholder}
            errMsg={errorMsg}
            maxLength={maxLength}
            required={required}
          />
        ) : (
          <TextAreaField
            fieldClassName="bg-system-bg03"
            ref={textareaRef}
            initialValue={initValue}
            readOnly={false}
            placeholder={placeholder}
            rows={rows}
            errorMsg={errorMsg}
            maxLength={maxLength}
            required={required}
          />
        )}
        <DialogClose className="absolute top-4 right-4" />
        <DialogFooter className="justify-end">
          <Button
            variant="primary"
            className="px-8"
            onClick={() => {
              handleSave();
            }}
          >
            {t('elements.elementDialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
